import * as React from "react";
import { CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";

export type SlidePreviewSlide = {
  id: string | number;
  title: string;
  src: string;
};

export type SlidePreviewEnlargeModalProps = {
  open: boolean;
  onClose: () => void;
  slides: SlidePreviewSlide[];
  activeSlide: SlidePreviewSlide | null;
  onActiveSlideChange: (slide: SlidePreviewSlide) => void;
  headerTitle: string;
  primaryActionLabel?: string;
  onPrimaryAction?: () => void;
  footerVariant?: "full" | "closeOnly" | "none";
};

export function SlidePreviewEnlargeModal({
  open,
  onClose,
  slides,
  activeSlide,
  onActiveSlideChange,
  headerTitle,
  primaryActionLabel,
  onPrimaryAction,
  footerVariant = "full",
}: SlidePreviewEnlargeModalProps) {
  const currentIndex = activeSlide == null ? -1 : slides.findIndex((s) => s.id === activeSlide.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < slides.length - 1;

  const handlePrev = React.useCallback(() => {
    if (!hasPrev || currentIndex < 0) return;
    onActiveSlideChange(slides[currentIndex - 1]!);
  }, [currentIndex, hasPrev, onActiveSlideChange, slides]);

  const handleNext = React.useCallback(() => {
    if (!hasNext || currentIndex < 0) return;
    onActiveSlideChange(slides[currentIndex + 1]!);
  }, [currentIndex, hasNext, onActiveSlideChange, slides]);

  return (
    <Modal
      open={open && activeSlide != null}
      onCancel={onClose}
      footer={null}
      width="min(1240px, 92vw)"
      centered
      destroyOnClose
      closable={false}
      styles={{
        mask: {
          backgroundColor: "rgba(0, 0, 0, 0.72)",
          backdropFilter: "blur(2px)",
        },
        content: {
          padding: 0,
          borderRadius: 10,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "94vh",
        },
        body: {
          padding: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        },
      }}
    >
      {activeSlide && (
        <div
          className="flex min-h-0 flex-1 flex-col"
          tabIndex={0}
          style={{
            outline: "none",
            height: "min(90vh, 1040px)",
            maxHeight: "94vh",
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") handlePrev();
            if (e.key === "ArrowRight") handleNext();
          }}
        >
          <div
            className="flex shrink-0 items-center justify-between gap-2 border-b border-white/[0.08] px-3 py-1.5"
            style={{ backgroundColor: "#001a2e", color: "white" }}
          >
            <div className="flex w-[7.5rem] shrink-0 items-center justify-start">
              <img
                src="assets/AlphaScript-logo.png"
                alt=""
                className="h-[18px] w-auto object-contain"
              />
            </div>
            <div className="min-w-0 flex-1 px-1">
              <span
                className="block truncate text-center font-medium text-white/90"
                style={{ fontSize: 13, lineHeight: 1.25, letterSpacing: "0.01em" }}
              >
                {headerTitle}
              </span>
            </div>
            <div className="flex w-[7.5rem] shrink-0 items-center justify-end">
              <Button
                type="text"
                size="small"
                aria-label="Close preview"
                onClick={onClose}
                icon={<CloseOutlined style={{ fontSize: 13 }} />}
                style={{ color: "rgba(255,255,255,0.85)", height: 28, minWidth: 28, paddingInline: 6 }}
              />
            </div>
          </div>

          <div
            className="flex min-h-0 flex-1 items-stretch justify-center"
            style={{
              padding: "6px 8px",
              background: "linear-gradient(165deg, #e8ecf2 0%, #dce2ea 45%, #d5dbe6 100%)",
            }}
          >
            <div
              className="flex min-h-0 w-full max-w-full flex-1 items-center justify-center"
              style={{
                borderRadius: 8,
                padding: "4px 6px",
                background: "linear-gradient(180deg, #fafbfc 0%, #f4f6f9 100%)",
                border: "1px solid rgba(0, 0, 0, 0.07)",
                boxShadow:
                  "0 12px 40px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.85)",
              }}
            >
              <img
                src={activeSlide.src}
                alt={activeSlide.title ?? "Slide preview"}
                style={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </div>
          </div>

          {footerVariant === "none" ? null : footerVariant === "closeOnly" ? (
            <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-200/90 bg-white px-4 py-2">
              <Button onClick={onClose} type="primary" style={{ minWidth: 140, fontWeight: 600, borderRadius: 6 }}>
                Close
              </Button>
            </div>
          ) : (
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-200/90 bg-white px-4 py-2">
              <Button onClick={onClose} style={{ minWidth: 72, fontWeight: 500, borderRadius: 6 }}>
                Close
              </Button>

              <div className="flex items-center gap-2">
                <Button icon={<LeftOutlined />} onClick={handlePrev} disabled={!hasPrev} style={{ borderRadius: 6 }}>
                  Previous
                </Button>
                {slides.length > 0 && currentIndex >= 0 ? (
                  <span style={{ fontSize: 12, color: "#888", minWidth: 56, textAlign: "center" }}>
                    {currentIndex + 1} / {slides.length}
                  </span>
                ) : null}
                <Button onClick={handleNext} disabled={!hasNext} style={{ borderRadius: 6 }}>
                  Next <RightOutlined />
                </Button>
              </div>

              <Button
                type="primary"
                onClick={onPrimaryAction}
                disabled={!onPrimaryAction}
                style={{
                  minWidth: 220,
                  fontWeight: 600,
                  borderRadius: 6,
                  background: "#1a7a4a",
                  borderColor: "#1a7a4a",
                  whiteSpace: "normal",
                  height: "auto",
                  lineHeight: 1.25,
                  paddingTop: 8,
                  paddingBottom: 8,
                }}
              >
                {primaryActionLabel ?? "Continue"}
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
